<template>
  <div class="pt-2 h-full">
    <NButtonToggleGroup
      v-model="portfolioVisualizationType"
      color="gray"
      dir="rtl"
      class="mb-1 text-right mx-1"
    >
      <template
        v-for="visualization of PORTFOLIO_VISUALIZATION"
        :key="visualization.value"
      >
        <NButtonToggle
          v-gid="{
            module: 'portfolio',
            screen: 'home',
            section: 'portfolioVisualizationTab',
            component: visualization.gidComponentName,
          }"
          :label="visualization.label"
        >
          <template #icon>
            <component :is="visualization.icon" size="20" />
          </template>
        </NButtonToggle>
      </template>
    </NButtonToggleGroup>

    <Error v-if="isErrorLoadingPortfolio" @retry="getPortfolio" />

    <div
      v-else-if="isLoadingSymbols || !showPortfolioMap"
      class="w-full h-full flex items-center justify-center"
    >
      <NLoading class="text-brand-primary" />
    </div>

    <EmptyList
      v-else-if="!portfolioSymbols.length"
      :title="t('modules.portfolio.emptyPortfolioTitle')"
      :body="t('modules.portfolio.emptyPortfolioMessage')"
      title-class="text-center mt-3"
      class="h-full flex flex-col justify-center items-center bg-neutral-primary"
    >
      <template #image>
        <Image src="empty_box" alt="not_found" class="w-28 h-max" />
      </template>

      <NButton
        v-gid="{
          module: 'portfolio',
          screen: 'home',
          section: 'portfolioVisualizationTab',
          component: 'brokerChangeButton',
        }"
        variant="flat"
        color="green"
        size="small"
        class="px-6"
        @click="openBrokerChangeDialog"
      >
        <NText weight="bold" variant="caption1">
          {{ t('modules.portfolio.brokerChangeTitle') }}
        </NText>
      </NButton>
    </EmptyList>

    <div v-else class="h-full">
      <div
        v-if="
          portfolioVisualizationType === PORTFOLIO_VISUALIZATION.donut.value
        "
        class="bg-neutral-primary h-full overflow-y-auto overflow-x-hidden"
      >
        <PortfolioDonutChart :symbols="portfolioSymbols" />
      </div>

      <div v-else class="flex flex-col h-full w-full">
        <PortfolioMarketMap :portfolio-map="portfolioMap" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { NegarDonutIcon, NegarMarketMapIcon } from '@negar/icons';
import { NButtonToggleGroup } from '@negar/core';
import { PortfolioItemWithPrices, usePortfolioMap } from '@core/portfolio';
import { sleep } from '@libs/utils';

import { PortfolioDonutChart, PortfolioMarketMap } from '@/modules/portfolio';
import { useSymbolDialogs } from '@/modules/symbol';

import EmptyList from '@/components/EmptyList.vue';
import Error from '@/components/Error.vue';
import Image from '@/components/Image.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const {
  portfolioMap,
  isLoadingSymbols,
  isErrorLoadingPortfolio,
  getPortfolio,
} = usePortfolioMap();
import { PortfolioItem, VirtualPortfolioItem } from '@libs/api-reference/src';
const { openBrokerChangeDialog } = useSymbolDialogs();
defineProps<{
  portfolioSymbols: VirtualPortfolioItem[] | PortfolioItemWithPrices[];
}>();
const portfolioVisualizationType = ref(0);

const showPortfolioMap = ref(false);

const PORTFOLIO_VISUALIZATION = {
  map: {
    value: 0,
    label: t('modules.portfolio.map'),
    icon: NegarMarketMapIcon,
    gidComponentName: 'portfolioMapButton',
  },
  donut: {
    value: 1,
    label: t('modules.portfolio.pieChart'),
    icon: NegarDonutIcon,
    gidComponentName: 'portfolioDonutChartButton',
  },
};

onMounted(async () => {
  // Wait for pain resize update
  await sleep(1000);

  showPortfolioMap.value = true;
});

const handlePortfolioVisualizationClick = () => {
  const newQuery = { ...route.query };

  delete newQuery.virtualPortfolio;

  router.push({ query: { ...newQuery, portfolio: 'true' } });
};
</script>
